import { z } from 'zod';

/**
 * Asking to attach a file, and saying the bytes arrived.
 *
 * TWO CALLS, NOT ONE, because the platform does not carry the bytes. The first
 * mints a key and a short-lived URL; the browser PUTs to the store; the second
 * tells the platform to go and look. Every fact about the stored object is
 * read back from the store at that second step, so nothing here describes the
 * file itself — only what the uploader is asking to be allowed to send.
 */

/**
 * The content types a support attachment may be.
 *
 * The SAME closed set `@dayon/storage`'s policy enforces, restated here as a
 * zod enum so the browser's file picker, the request validator and the signed
 * upload all refuse the same list. It is duplicated rather than imported
 * because `@dayon/contracts` is shared with browser bundles and must not pull
 * an AWS SDK in behind it; the storage package's test asserts the two agree,
 * which is what stops the duplication becoming a divergence.
 */
export const attachmentContentType = z.enum([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'application/pdf',
]);
export type AttachmentContentType = z.infer<typeof attachmentContentType>;

export const requestAttachmentUpload = z.object({
  /**
   * What the uploader calls the file. Kept as DATA — rendered on a screen and
   * offered as a download name — and never used to build the object key.
   */
  filename: z.string().trim().min(1).max(255),
  contentType: attachmentContentType,
  /**
   * The size the browser believes the file is.
   *
   * ADVISORY, and treated as such: it lets the platform refuse an oversized
   * file before signing anything, which saves the uploader a wasted transfer.
   * It is NOT what gets recorded — the size on the row is read back from the
   * store, because the only party that can be believed about how many bytes
   * arrived is the one that received them.
   */
  sizeBytes: z.number().int().positive().max(10 * 1024 * 1024),
});
export type RequestAttachmentUpload = z.infer<typeof requestAttachmentUpload>;

export const attachmentUploadTicket = z.object({
  attachmentId: z.string(),
  url: z.string(),
  /** Headers the browser must replay on the PUT for the signature to hold. */
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type AttachmentUploadTicket = z.infer<typeof attachmentUploadTicket>;

export const attachmentSummary = z.object({
  id: z.string(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  uploadedBy: z.string(),
  uploadedByKind: z.enum(['PLAYER', 'STAFF']),
  createdAt: z.string(),
});
export type AttachmentSummary = z.infer<typeof attachmentSummary>;

export const attachmentDownload = z.object({
  url: z.string(),
  expiresAt: z.string(),
  filename: z.string(),
});
export type AttachmentDownload = z.infer<typeof attachmentDownload>;
